/**
 * Service Routes Configuration
 *
 * DTCP-specific citizen service pages only. Non-DTCP modules
 * (Property Tax, Water Charges, Trade License, Advertisement Tax,
 * Marriage Registration, Sewerage Connection, Water-Sewerage, Land Tax,
 * Birth/Death) were removed since DTCP handles only town & country
 * planning and display-device licensing.
 */

import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ServiceLayout } from '../components/services/ServiceLayout';
import { ServicesSection } from '../components/feature/ServicesSection';
import { TableSkeleton } from '@/components/base/Skeleton';

const Loading = () => (
  <TableSkeleton />
);

function lazyLoad(importFn: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazy(importFn);
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
}

export const serviceRoutes: RouteObject[] = [
  {
    path: '/services',
    element: <ServiceLayout />,
    children: [
      // Services listing page (index)
      { index: true, element: <ServicesSection /> },

      // Building Permission (DTCP core)
      { path: 'building-permission', element: lazyLoad(() => import('../pages/services/BuildingPermissionPage')) },
      { path: 'building-permission/apply', element: lazyLoad(() => import('../pages/services/building-permission/ApplyBuildingPermission')) },

      // Right of Way (DTCP planning)
      { path: 'right-of-way/apply', element: lazyLoad(() => import('../pages/services/right-of-way/ApplyRightOfWay')) },

      // Third-Party Verification
      { path: 'third-party-verification/verify', element: lazyLoad(() => import('../pages/services/third-party-verification/VerifyThirdParty')) },

      // Display Devices (GO Ms.No.253) — core DTCP
      { path: 'display-devices/faq', element: lazyLoad(() => import('../pages/services/DisplayDevicesFaqPage')) },

      // ── LRS / BPS ──────────────────────────────────────────────────
      // Information pages only. Applications are filed on the schemes' own
      // portals; this ERP only keeps the books from the data they push to us,
      // so there is no apply wizard, no citizen account and no status lookup
      // here — those all live on the scheme portals.
      { path: 'bps', element: lazyLoad(() => import('../pages/services/BpsPage')) },
      { path: 'lrs', element: lazyLoad(() => import('../pages/services/LrsPage')) },

      // Demolition of unauthorised constructions / layouts
      { path: 'demolition', element: lazyLoad(() => import('../pages/services/DemolitionPage')) },

      // Geo-Tagging of Municipal Assets
      { path: 'municipal-assets', element: lazyLoad(() => import('../pages/services/MunicipalAssetsPage')) },

      // Catch-all for services not yet built — shows Coming Soon
      { path: '*', element: <ComingSoonService /> },
    ]
  }
];

function ComingSoonService() {
  const serviceName = window.location.pathname.split('/services/')[1]?.replace(/-/g, ' ') || 'Service';
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-24 h-24 bg-amber-50 rounded-full flex items-center justify-center mb-6">
        <i className="ri-tools-line text-amber-500 text-5xl" />
      </div>
      <h1 className="text-2xl font-bold text-gray-800 capitalize mb-2">{serviceName}</h1>
      <p className="text-gray-500 max-w-md mb-6">This service is currently under development and will be available soon. Please check back later.</p>
      <a href="/" className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors inline-flex items-center gap-2">
        <i className="ri-home-line" /> Back to Home
      </a>
    </div>
  );
}
